import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { ConfiguratorInput } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { ConfigurationCheckpointApi } from "src/api/generated/erp/configurator/checkpoints/api/configurationCheckpointApi.ts";
import {
	ConfigurationCheckpointContext,
	ConfigurationCheckpointContextValue,
} from "src/components/views/erp/configurator/configuratorForm/checkpoints/ConfigurationCheckpointContext.ts";
import {
	ConfigurationCheckpointEntry,
	ConfigurationCheckpointState,
} from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";

interface ConfigurationCheckpointProviderProps {
	configurationSessionId: string;
	configuratorType: PartConfiguratorType;
	checkpointState: ConfigurationCheckpointState;
	setCheckpointState: Dispatch<SetStateAction<ConfigurationCheckpointState>>;
	children: ReactNode;
}

export const ConfigurationCheckpointProvider = ({
	configurationSessionId,
	configuratorType,
	checkpointState,
	setCheckpointState,
	children,
}: ConfigurationCheckpointProviderProps) => {
	const [isCreating, setIsCreating] = useState(false);

	const contextValue: ConfigurationCheckpointContextValue = {
		state: checkpointState,
		entries: checkpointState.entries,
		createCheckpoint,
		isCreating,
	};

	return (
		<ConfigurationCheckpointContext.Provider value={contextValue}>
			{children}
		</ConfigurationCheckpointContext.Provider>
	);

	async function createCheckpoint(configuratorInput: ConfiguratorInput, currentInputVersion: number): Promise<void> {
		setIsCreating(true);
		try {
			const result = await ConfigurationCheckpointApi.createConfigurationCheckpoint({
				configurationSessionId,
				configuratorType,
				configuratorInput,
				previousCheckpointPropertyValues: checkpointState.previousPropertyValues,
			});

			const price = result.price ?? null;
			const previousCheckpointPrice = checkpointState.entries.at(-1)?.price;
			const previousSalesPriceWithVat =
				price == null ? null
				: previousCheckpointPrice == null ? price.configurationSavedPriceWithVat
				: previousCheckpointPrice.sellingCurrencyCode === price.sellingCurrencyCode ?
					previousCheckpointPrice.salesPriceWithVat
				:	null;

			const entry: ConfigurationCheckpointEntry =
				price == null ?
					{
						description: result.description,
						price: null,
					}
				:	{
						description: result.description,
						price: {
							salesPriceWithVat: price.salesPriceWithVat,
							salesPriceDiff:
								previousSalesPriceWithVat != null ? price.salesPriceWithVat - previousSalesPriceWithVat : null,
							sellingCurrencyCode: price.sellingCurrencyCode,
						},
					};

			setCheckpointState((prev) => ({
				previousPropertyValues: result.resolvedPropertyValues,
				entries: [...prev.entries, entry],
				lastCheckpointInputVersion: currentInputVersion,
			}));
		} finally {
			setIsCreating(false);
		}
	}
};
